import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, ShoppingBag, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchCart } = useCart();
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type'); // 'onetime' or 'subscription'
  const hasPendingSubscription = searchParams.get('pending_subscription') === 'true';
  
  const [status, setStatus] = useState('loading'); // loading, success, error, pending_sepa
  const [paymentData, setPaymentData] = useState(null);
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [countdown, setCountdown] = useState(8);
  const [isSettingUpSepa, setIsSettingUpSepa] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API}/checkout/status/${sessionId}`, {
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error('Failed to check status');
        }

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          // Check if there's a pending subscription to set up
          if (hasPendingSubscription && paymentType === 'onetime') {
            // Fetch pending subscription details
            const cartId = data.metadata?.cart_id;
            if (cartId) {
              const pendingRes = await fetch(`${API}/checkout/pending-subscription/${cartId}`, {
                credentials: 'include'
              });
              if (pendingRes.ok) {
                const pendingData = await pendingRes.json();
                if (pendingData.has_pending) {
                  setPendingSubscription(pendingData);
                  setStatus('pending_sepa');
                  fetchCart();
                  return;
                }
              }
            }
          }
          
          setStatus('success');
          fetchCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Status check error:', error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, fetchCart, hasPendingSubscription, paymentType]);

  // Handle SEPA subscription setup
  const handleSetupSepa = async () => {
    setIsSettingUpSepa(true);
    try {
      const cartId = paymentData?.metadata?.cart_id;
      const response = await fetch(`${API}/checkout/setup-subscription-sepa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          cart_id: cartId,
          origin_url: window.location.origin
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create SEPA session');
      }
      
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('SEPA setup error:', error);
      setIsSettingUpSepa(false);
    }
  };

  // Countdown and redirect for success
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/account?tab=orders');
    }
  }, [status, countdown, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-emerald-400 animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-white mb-2">Vérification du paiement...</h2>
          <p className="text-white/60">Veuillez patienter quelques instants</p>
        </div>
      </div>
    );
  }

  // PENDING SEPA: Products paid, need to set up subscription
  if (status === 'pending_sepa') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4">
        <div className="max-w-lg w-full">
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 text-center">
            {/* Success icon for products */}
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            
            <h2 className="text-2xl font-semibold text-white mb-2">
              Paiement des produits confirmé !
            </h2>
            <p className="text-white/60 mb-6">
              Vos produits et l'installation ont été payés avec succès.
            </p>

            {/* SEPA Setup Card */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 mb-6 text-left">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium mb-1">Étape 2 : Configurer l'abonnement 4G</h3>
                  <p className="text-white/50 text-sm mb-3">
                    Pour activer votre connexion 4G, configurez le prélèvement SEPA. 
                    C'est plus fiable qu'une carte bancaire (pas d'expiration).
                  </p>
                  <div className="flex items-center gap-2 text-blue-400 text-sm">
                    <span className="font-mono font-bold">
                      {pendingSubscription?.subscription_amount?.toFixed(2) || '0.00'} €
                    </span>
                    <span className="text-white/40">/mois</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SEPA Benefits */}
            <div className="grid grid-cols-2 gap-3 mb-6 text-left">
              <div className="flex items-center gap-2 text-sm text-white/60">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Pas d'expiration</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Prélèvement sécurisé</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Annulable à tout moment</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Protection bancaire</span>
              </div>
            </div>

            <Button 
              onClick={handleSetupSepa}
              disabled={isSettingUpSepa}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3"
            >
              {isSettingUpSepa ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Redirection...
                </>
              ) : (
                <>
                  Configurer le prélèvement SEPA
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>

            <p className="text-white/40 text-xs mt-4">
              Vous serez redirigé vers une page sécurisée Stripe pour saisir votre IBAN.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-white mb-2">Erreur de paiement</h2>
            <p className="text-white/60 mb-6">
              Une erreur s'est produite lors du traitement de votre paiement. 
              Veuillez réessayer ou nous contacter.
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/checkout">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-400">
                  Réessayer
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center py-12 px-4">
      <div className="max-w-lg w-full text-center">
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8">
          {/* Success Animation */}
          <div className="relative mb-6">
            <div className="w-24 h-24 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-16 h-16 text-emerald-400" />
            </div>
          </div>

          <h2 className="text-3xl font-semibold text-white mb-2">Merci pour votre commande !</h2>
          <p className="text-white/60 mb-6">
            {paymentType === 'subscription' 
              ? "Votre abonnement 4G a été activé avec succès. Le prélèvement SEPA sera effectué chaque mois."
              : "Votre paiement a été confirmé avec succès. Vous recevrez un email de confirmation sous peu."
            }
          </p>

          {/* Order Summary */}
          {paymentData && (
            <div className="bg-black/40 border border-white/10 rounded-xl p-4 mb-6 text-left">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
                <Package className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-white font-medium">
                    {paymentType === 'subscription' ? 'Abonnement activé' : 'Commande confirmée'}
                  </p>
                  <p className="text-sm text-white/50">
                    {paymentType === 'subscription' ? 'Prélèvement SEPA configuré' : 'Livraison sous 3-5 jours ouvrés'}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-white/50">Montant {paymentType === 'subscription' ? '/mois' : 'payé'}</span>
                  <span className="text-white font-mono text-lg">
                    {(paymentData.amount_total / 100).toFixed(2)} €
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Statut</span>
                  <span className="text-emerald-400 font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    {paymentType === 'subscription' ? 'Actif' : 'Payé'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Redirect Notice */}
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 mb-6">
            <p className="text-sm text-emerald-400">
              Redirection automatique vers vos commandes dans <strong>{countdown}</strong> secondes...
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Link to="/account?tab=orders">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-400 h-12">
                <ShoppingBag className="w-5 h-5 mr-2" />
                Voir mes commandes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/shop">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 h-12">
                Continuer mes achats
              </Button>
            </Link>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 flex items-center justify-center gap-6 text-sm text-white/50">
          <span>✓ Paiement sécurisé</span>
          <span>✓ Garantie 4 ans</span>
          <span>✓ Support 7j/7</span>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
